// 네이티브 앱(Capacitor) FCM 푸시 클라이언트.
// 안드로이드 WebView 는 웹 푸시(PushManager)를 지원하지 않아, 네이티브 앱에서는 FCM 기기 토큰으로
// 알림을 받는다(서버 /push/device-token 에 등록, 발송은 서버 FCM HTTP v1). 웹/PWA 는 기존 pushClient 사용.
//  - 알림 채널 "orders" 는 서버 발송의 android.notification.channel_id 와 반드시 일치해야 한다.
//  - 토큰은 회전될 수 있어, 앱 시작 시(NativeBridge) 활성 상태면 재등록해 최신 토큰을 서버에 유지한다.

import { Capacitor, registerPlugin } from "@capacitor/core";
import { PushNotifications, type PermissionStatus } from "@capacitor/push-notifications";
import { api } from "@/api/client.ts";
import { isNativeApp } from "@/shared/platform.ts";

// Android 일부 기기에서 공식 PushNotifications.checkPermissions()가 프로세스를 종료시키는
// Capacitor 8 문제가 있어, Android 권한 확인/요청만 앱 전용 네이티브 플러그인으로 우회한다.
// iOS는 기존 공식 플러그인 경로를 유지한다.
interface OpenChuksanPushPermissionPlugin {
  checkPermissions(): Promise<PermissionStatus>;
  requestPermissions(): Promise<PermissionStatus>;
  createOrderChannel(): Promise<void>;
  register(): Promise<{ value: string }>;
  unregister(): Promise<void>;
}

const OpenChuksanPushPermission = registerPlugin<OpenChuksanPushPermissionPlugin>(
  "OpenChuksanPushPermission",
);

// 현재 플랫폼의 알림 권한 상태를 조회하되 Android에서는 강제 종료가 없는 앱 전용 브리지를 사용한다.
export async function checkNativePushPermissions(): Promise<PermissionStatus> {
  return Capacitor.getPlatform() === "android"
    ? OpenChuksanPushPermission.checkPermissions()
    : PushNotifications.checkPermissions();
}

async function requestNativePushPermissions(): Promise<PermissionStatus> {
  return Capacitor.getPlatform() === "android"
    ? OpenChuksanPushPermission.requestPermissions()
    : PushNotifications.requestPermissions();
}

// 이 기기에서 알림을 켰는지(서버 등록까지 마쳤는지) 플래그 + 마지막 등록 토큰(끄기 시 서버 삭제용).
const ENABLED_FLAG = "oo-fcm-enabled";
const TOKEN_KEY = "oo-fcm-token";

export type NativePushResult = { ok: true } | { ok: false; reason: "denied" | "error" };

// registration 리스너는 앱 수명 동안 1회만 배선한다(중복 등록 방지).
//  - register() 호출 결과(토큰)는 이벤트로 오므로, 진행 중인 요청의 resolve 를 모듈 상태로 연결한다.
//  - 토큰 회전 등 요청 없이 이벤트가 와도, 활성 상태면 서버에 조용히 재등록한다.
let listenersWired = false;
let pendingTokenResolve: ((token: string | null) => void) | null = null;

async function wireTokenListeners(): Promise<void> {
  if (listenersWired) {
    return;
  }
  listenersWired = true;
  await PushNotifications.addListener("registration", (t) => {
    const resolve = pendingTokenResolve;
    pendingTokenResolve = null;
    if (resolve) {
      resolve(t.value);
      return;
    }
    // 요청 없이 온 이벤트 = 토큰 회전. 알림이 켜져 있으면 서버에 최신 토큰을 반영한다.
    if (localStorage.getItem(ENABLED_FLAG)) {
      localStorage.setItem(TOKEN_KEY, t.value);
      void api
        .post("/push/device-token", { token: t.value, platform: "android" })
        .catch(() => undefined);
    }
  });
  await PushNotifications.addListener("registrationError", () => {
    const resolve = pendingTokenResolve;
    pendingTokenResolve = null;
    resolve?.(null);
  });
}

// FCM register() 를 호출하고 registration 이벤트로 토큰을 받는다. 실패/타임아웃(10초)이면 null.
async function registerAndGetToken(): Promise<string | null> {
  if (Capacitor.getPlatform() === "android") {
    try {
      const token = await OpenChuksanPushPermission.register();
      return token.value || null;
    } catch {
      return null;
    }
  }

  await wireTokenListeners();
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      if (pendingTokenResolve) {
        pendingTokenResolve = null;
        resolve(null);
      }
    }, 10_000);
    pendingTokenResolve = (token) => {
      clearTimeout(timer);
      resolve(token);
    };
    void PushNotifications.register().catch(() => {
      clearTimeout(timer);
      pendingTokenResolve = null;
      resolve(null);
    });
  });
}

// 알림 켜기: 권한 요청 -> 채널 생성 -> FCM 등록 -> 서버에 토큰 저장.
export async function enableNativePush(): Promise<NativePushResult> {
  if (!isNativeApp) {
    return { ok: false, reason: "error" };
  }
  try {
    // Android 13+ 는 알림이 런타임 권한이다. prompt 상태면 시스템 다이얼로그가 뜬다.
    let perm = await checkNativePushPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await requestNativePushPermissions();
    }
    if (perm.receive !== "granted") {
      return { ok: false, reason: "denied" };
    }
    // 주문 알림 채널(멱등 - 이미 있으면 갱신). 소리/헤드업 표시를 위해 중요도 HIGH(4).
    if (Capacitor.getPlatform() === "android") {
      await OpenChuksanPushPermission.createOrderChannel();
    }
    const token = await registerAndGetToken();
    if (!token) {
      return { ok: false, reason: "error" };
    }
    await api.post("/push/device-token", { token, platform: "android" });
    localStorage.setItem(ENABLED_FLAG, "1");
    localStorage.setItem(TOKEN_KEY, token);
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

// 알림 끄기: 서버 토큰 삭제 + FCM 등록 해제.
export async function disableNativePush(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  localStorage.removeItem(ENABLED_FLAG);
  localStorage.removeItem(TOKEN_KEY);
  if (token) {
    await api.post("/push/device-token/remove", { token }).catch(() => undefined);
  }
  if (Capacitor.getPlatform() === "android") {
    await OpenChuksanPushPermission.unregister().catch(() => undefined);
  } else {
    await PushNotifications.unregister().catch(() => undefined);
  }
}

// 이 기기에서 알림이 켜져 있는가(마이페이지 토글 초기 상태).
export function isNativePushEnabled(): boolean {
  return isNativeApp && !!localStorage.getItem(ENABLED_FLAG);
}

// 앱 시작 시 동기화(NativeBridge 에서 호출). 알림이 켜져 있으면 재등록해 회전된 토큰을 서버에 반영한다.
//  - 로그인 상태가 아니면 서버 등록이 401 이므로 건너뛴다(다음 로그인 후 시작 시 반영).
export async function syncNativePushOnLaunch(loggedIn: boolean): Promise<void> {
  if (!isNativeApp || !loggedIn || !localStorage.getItem(ENABLED_FLAG)) {
    return;
  }
  try {
    const perm = await checkNativePushPermissions();
    if (perm.receive !== "granted") {
      return; // 사용자가 OS 설정에서 껐음 - 서버 토큰은 발송 실패(UNREGISTERED 아님)로 남지만 무해.
    }
    const token = await registerAndGetToken();
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      await api.post("/push/device-token", { token, platform: "android" });
    }
  } catch {
    // 시작 동기화 실패는 조용히 무시(다음 실행에서 재시도).
  }
}
